import React, { FC, useMemo } from 'react';
import { GetCustomersV2ColumnInfo, GetCustomersV2ListItem } from 'containers/customers/customers.model';
import { getAmountWithCurrency } from 'utils/amountUtils';
import DateUtils from 'utils/dateUtils';
import { GenericTable } from 'components';
import { getStringFiller } from 'utils/stringUtils';
import {
  selectCurrentTeamCustomersPermissions,
  selectCurrentTeamInvoicePermissions,
  selectUserDetails
} from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { TableColumn } from 'components/generic-table/types';
import CustomerRowActions from './CustomerRowActions';
import { clevertapEvents } from 'types/baseTypes';
import { logAnalyticEvent } from 'utils/analytics';
import { useNavigate } from 'react-router-dom';
import CustomAvatar from 'components/avatar/CustomAvatar';
import { getInitials } from 'utils/commonUtils';

interface CustomersTableProps {
  customersList: GetCustomersV2ListItem[];
  onCustomerClick: (customerId: string) => void;
  onEditCustomer: (customerId: string) => void;
  columnInfo?: GetCustomersV2ColumnInfo[];
  onSortChange?: (sortBy: string, sortOrder: string) => void;
  currentSortBy?: string;
  currentSortOrder?: string;
  stickyHorizontalScrollbar?: { bottomOffsetPx: number };
}

const CustomersTable: FC<CustomersTableProps> = ({
  customersList,
  onCustomerClick,
  onEditCustomer,
  columnInfo,
  onSortChange,
  currentSortBy = 'name',
  currentSortOrder = 'ASC',
  stickyHorizontalScrollbar
}) => {
  const customerPermissions = useAppSelector(selectCurrentTeamCustomersPermissions);
  const invoicePermissions = useAppSelector(selectCurrentTeamInvoicePermissions);
  const userDetails = useAppSelector(selectUserDetails);

  const navigate = useNavigate();

  const getFullName = (customer: GetCustomersV2ListItem) => {
    if (!customer.name) {
      return '-';
    }
    return customer.name;
  };

  const formatDate = (date: string) => {
    if (!date) {
      return '-';
    }
    return DateUtils.getDateInFormat({
      date: new Date(date),
      dateFormat: 'dd MMM yyyy'
    });
  };

  const isEditCustomerAllowed = (merchantId: string) => {
    if (customerPermissions.editCustomersCreatedByTeam) {
      return true;
    }
    if (customerPermissions.editExistingCustomers) {
      const isSelfCreatedCustomer = merchantId === userDetails?.id;
      return isSelfCreatedCustomer;
    }
    return false;
  };

  const handleNewInvoice = (customerId: string) => {
    logAnalyticEvent(clevertapEvents.webCustomersListMMNewInvoice, {
      'Customer ID': customerId
    });
    navigate('/invoices/create?customerId=' + customerId);
  };

  const columns = useMemo<TableColumn<GetCustomersV2ListItem>[]>(() => {
    const baseColumns: TableColumn<GetCustomersV2ListItem>[] = [
      {
        id: 'name',
        header: 'Name',
        accessor: customer => (
          <div className="break-words text-sbase text-primaryText line-clamp-1" title={getFullName(customer)}>
            {getFullName(customer)}
          </div>
        ),
        width: 'min-w-[150px] w-[150px]',
        fixed: 'left',
        sortable: true
      },
      {
        id: 'phone',
        header: 'Phone number',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">{getStringFiller(customer.phone)}</div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'email',
        header: 'Email ID',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">{getStringFiller(customer.email)}</div>
        ),
        width: 'w-[200px]',
        sortable: true
      },
      {
        id: 'totalInvoicedAmount',
        header: 'Total invoiced',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">
            {customer.totalInvoicedAmount ? getAmountWithCurrency(customer.totalInvoicedAmount) : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'outstandingBalanceAmount',
        header: 'Outstanding balance',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">
            {customer.outstandingBalanceAmount ? getAmountWithCurrency(customer.outstandingBalanceAmount) : '-'}
          </div>
        ),
        width: 'w-[170px]',
        sortable: true
      },
      {
        id: 'lastActivityAt',
        header: 'Last activity',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">{formatDate(customer.lastActivityAt)}</div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'cardOnFileCount',
        header: 'Card on file',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">
            {customer.cardOnFileCount ? customer.cardOnFileCount : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'totalPaidAmount',
        header: 'Total paid',
        accessor: customer => (
          <div className="text-sbase text-primaryText">
            {customer.totalPaidAmount ? getAmountWithCurrency(customer.totalPaidAmount) : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'jobCount',
        header: 'Job count',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">{customer.jobCount ? customer.jobCount : '-'}</div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'invoiceCount',
        header: 'Invoice count',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">
            {customer.invoiceCount ? customer.invoiceCount : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'createdAt',
        header: 'Created date',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">{formatDate(customer.createdAt)}</div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'createdBy',
        header: 'Created by',
        accessor: customer => (
          <div className="flex items-center space-x-2">
            <CustomAvatar
              src={customer.createdBy?.profilePictureURL}
              label={getInitials(customer.createdBy?.name || '')}
              height="h-6"
              width="w-6"
              className="rounded-full"
              fontStyle="text-[8px]"
            />
            <span className="text-sbase text-primaryText">{customer.createdBy?.name || '-'}</span>
          </div>
        ),
        width: 'w-[200px]'
      },
      {
        id: 'disputeLossAmount',
        header: 'Dispute losses',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">
            {customer.disputeLossAmount ? getAmountWithCurrency(customer.disputeLossAmount) : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'refundAmount',
        header: 'Refunds',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">
            {customer.refundAmount ? getAmountWithCurrency(customer.refundAmount) : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'totalTipAmount',
        header: 'Total tip',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">
            {customer.totalTipAmount ? getAmountWithCurrency(customer.totalTipAmount) : '-'}
          </div>
        ),
        width: 'w-[150px]',
        sortable: true
      },
      {
        id: 'firstPaymentAt',
        header: 'First payment date',
        accessor: customer => <div className="text-sbase text-primaryText">{formatDate(customer.firstPaymentAt)}</div>,
        width: 'w-[160px]',
        sortable: true
      },
      {
        id: 'lastPaymentAt',
        header: 'Last payment date',
        accessor: customer => <div className="text-sbase text-primaryText">{formatDate(customer.lastPaymentAt)}</div>,
        width: 'w-[160px]',
        sortable: true
      },
      {
        id: 'serviceAddress',
        header: 'Service address',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">{getStringFiller(customer.serviceAddress)}</div>
        ),
        width: 'w-[200px]'
      },
      {
        id: 'note',
        header: 'Note',
        accessor: customer => (
          <div className="truncate text-sbase text-primaryText">{getStringFiller(customer.note)}</div>
        ),
        width: 'w-[200px]'
      },
      {
        id: 'actions',
        header: '',
        accessor: customer => (
          <CustomerRowActions
            onEditCustomer={() => onEditCustomer(customer.id)}
            showEdit={isEditCustomerAllowed(customer.createdBy?.id)}
            showNewInvoice={invoicePermissions?.createInvoice && customerPermissions.createNewCustomers}
            onNewInvoice={() => handleNewInvoice(customer.id)}
          />
        ),
        fixed: 'right',
        width: 'w-[100px]'
      }
    ];

    return baseColumns;
  }, [customerPermissions, invoicePermissions, userDetails?.id, columnInfo, onEditCustomer]);

  return (
    <div className="w-full">
      <GenericTable
        data={customersList}
        columns={columns}
        onRowClick={customer => onCustomerClick(customer.id)}
        className="w-full"
        enableRowHover={true}
        textColumns={['name', 'phone', 'email']}
        columnInfo={columnInfo}
        sorting={{
          sortBy: currentSortBy,
          sortOrder: currentSortOrder,
          enableMultiSort: false,
          onSortChange: (sortBy: string, sortOrder: string) => {
            onSortChange?.(sortBy, sortOrder);
          }
        }}
        stickyHorizontalScrollbar={stickyHorizontalScrollbar}
      />
    </div>
  );
};

export default CustomersTable;
